import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, EmptyContent, Flex } from '@/components/ui';
import {
  useDeleteStudyFile,
  useStudyFiles,
} from '@/services/studyFilesHooks';
import { uploadStudyFile } from '@/services/studyFilesApi';
import { StudyFile } from '@/types/studyFiles';
import type { StudyFilesTableProps } from './types';
import { toast } from 'sonner';
import { runPoolSettled } from '@/utils/concurrencyPool';
import { getUploadMaxConcurrent } from '@/utils/uploadPreferences';

import {
  IconAlertTriangleFilled,
  IconCloudUpload,
  IconUpload,
} from '@tabler/icons-react';
import { useDropzone } from 'react-dropzone';

import StudyFilesTable from './StudyFilesTable';
import { StudyFilesUploadConfirmationModal } from './StudyFilesUploadConfirmationModal';
import {
  StudyFilesManagerProps,
  FileUploadProgress,
  FileUploadStatus,
} from './types';

const StudyFilesManager: React.FC<StudyFilesManagerProps> = ({ className }) => {
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [fileProgress, setFileProgress] = useState<FileUploadProgress[]>([]);
  const [currentUploadingFileIndex, setCurrentUploadingFileIndex] = useState<number | undefined>(undefined);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadAbortControllersRef = useRef<Map<number, AbortController>>(new Map());
  const batchAbortControllerRef = useRef<AbortController | null>(null);
  const cancelUploadRequestedRef = useRef(false);
  const pendingProgressRef = useRef<Map<number, number>>(new Map());
  const progressFlushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Throttled progress update to avoid O(n) setState on every progress event
  const throttleProgressMs = 200;
  const scheduleProgressFlush = useCallback(() => {
    if (progressFlushTimerRef.current) return;
    progressFlushTimerRef.current = setTimeout(() => {
      const pending = new Map(pendingProgressRef.current);
      pendingProgressRef.current.clear();
      progressFlushTimerRef.current = null;
      if (pending.size === 0) return;
      setFileProgress(prev =>
        prev.map((fp, idx) => {
          const p = pending.get(idx);
          return p !== undefined ? { ...fp, progress: p } : fp;
        })
      );
    }, throttleProgressMs);
  }, []);

  const throttledProgressUpdate = useCallback(
    (index: number, progress: number) => {
      pendingProgressRef.current.set(index, progress);
      scheduleProgressFlush();
    },
    [scheduleProgressFlush]
  );

  useEffect(() => () => {
    if (progressFlushTimerRef.current) {
      clearTimeout(progressFlushTimerRef.current);
      progressFlushTimerRef.current = null;
    }
  }, []);

  // API hooks - memoize filters to prevent unnecessary requests
  const studyFilesFilters = useMemo(
    () => ({
      page: pagination.pageIndex + 1, // API uses 1-based pagination
      per_page: pagination.pageSize,
      // TODO: Enable whitelist filtering when backend supports it
      // For canManageStudyFiles users, filter out files that are in whitelist
      // exclude_whitelisted: hasWhitelistAccess, // This would be enabled when backend supports it
    }),
    [pagination.pageIndex, pagination.pageSize]
  );

  const { data, isLoading, error, refetch } = useStudyFiles(studyFilesFilters);
  const files = data?.files || [];
  const paginationInfo = data?.pagination;
  // Keep the last known pagination info during loading to avoid UI flicker like "Page 4 of 1"
  // when the response pagination is temporarily undefined.
  const lastPaginationInfoRef = useRef<StudyFilesTableProps['pagination']>(undefined);
  useEffect(() => {
    if (paginationInfo) {
      lastPaginationInfoRef.current = paginationInfo;
    }
  }, [paginationInfo]);
  const effectivePaginationInfo = paginationInfo ?? lastPaginationInfoRef.current;
  const deleteMutation = useDeleteStudyFile();

  // Sync requested pagination with server response to avoid invalid UI states
  // (e.g. "Page 4 of 1" when the backend clamps current_page to 1).
  useEffect(() => {
    if (!paginationInfo) return;
    const serverPageIndex = Math.max((paginationInfo.current_page ?? 1) - 1, 0);
    const serverPageSize = paginationInfo.per_page ?? pagination.pageSize;

    setPagination(prev => {
      if (
        prev.pageIndex === serverPageIndex &&
        prev.pageSize === serverPageSize
      ) {
        return prev;
      }
      return {
        ...prev,
        pageIndex: serverPageIndex,
        pageSize: serverPageSize,
      };
    });
  }, [paginationInfo?.current_page, paginationInfo?.per_page, pagination.pageSize]);

  const handleFilesSelected = useCallback((selectedFiles: File[]) => {
    if (selectedFiles.length === 0) return;
    setSelectedFiles(selectedFiles);
    setShowUploadModal(true);
  }, []);

  const handleDeleteFile = useCallback(
    async (file: StudyFile) => {
      setDeleteError(null);

      try {
        await deleteMutation.mutateAsync(file.id);
      } catch (error) {
        setDeleteError(
          error instanceof Error ? error.message : 'Delete failed'
        );
      }
    },
    [deleteMutation]
  );

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const handlePaginationChange = useCallback(
    (newPagination: { pageIndex: number; pageSize: number }) => {
      setPagination(prev => {
        if (
          prev.pageIndex === newPagination.pageIndex &&
          prev.pageSize === newPagination.pageSize
        ) {
          return prev;
        }
        return newPagination;
      });
    },
    []
  );

  // Upload handlers
  const handleConfirmUpload = useCallback(async () => {
    if (selectedFiles.length === 0) return;

    cancelUploadRequestedRef.current = false;

    // Initialize file progress
    const initialProgress: FileUploadProgress[] = selectedFiles.map(file => ({
      file,
      status: 'pending' as FileUploadStatus,
      progress: 0,
    }));
    setFileProgress(initialProgress);
    setCurrentUploadingFileIndex(0);

    try {
      const maxConcurrent = getUploadMaxConcurrent();
      const activeIndices = new Set<number>();

      const updateCurrentUploadingIndex = () => {
        if (activeIndices.size === 0) {
          setCurrentUploadingFileIndex(undefined);
          return;
        }
        const minIndex = Math.min(...Array.from(activeIndices));
        setCurrentUploadingFileIndex(minIndex);
      };

      uploadAbortControllersRef.current.clear();
      batchAbortControllerRef.current = new AbortController();

      let successCount = 0;
      let errorCount = 0;

      const indices = selectedFiles.map((_, idx) => idx);

      await runPoolSettled(
        indices,
        async (i: number) => {
          if (cancelUploadRequestedRef.current) {
            throw new DOMException('Aborted', 'AbortError');
          }

          const file = selectedFiles[i];
          if (!file) return;

          activeIndices.add(i);
          updateCurrentUploadingIndex();

          setFileProgress(prev =>
            prev.map((fp, idx) =>
              idx === i
                ? { ...fp, status: 'uploading' as FileUploadStatus, progress: 0 }
                : fp
            )
          );

          const controller = new AbortController();
          uploadAbortControllersRef.current.set(i, controller);

          try {
            await uploadStudyFile(
              { file },
              (progressEvent) => {
                if (progressEvent.total) {
                  const progress = Math.round(
                    (progressEvent.loaded * 100) / progressEvent.total
                  );
                  throttledProgressUpdate(i, progress);
                }
              },
              { signal: controller.signal }
            );

            successCount += 1;
            pendingProgressRef.current.delete(i);
            setFileProgress(prev =>
              prev.map((fp, idx) =>
                idx === i
                  ? {
                      ...fp,
                      status: 'completed' as FileUploadStatus,
                      progress: 100,
                    }
                  : fp
              )
            );
          } catch (error) {
            const isCancelled =
              (error instanceof Error &&
                (error.name === 'CanceledError' ||
                  error.name === 'AbortError')) ||
              (error &&
                typeof error === 'object' &&
                'code' in error &&
                (error as { code?: unknown }).code === 'ERR_CANCELED');

            if (isCancelled || cancelUploadRequestedRef.current) {
              pendingProgressRef.current.delete(i);
              setFileProgress(prev =>
                prev.map((fp, idx) =>
                  idx === i
                    ? { ...fp, status: 'cancelled' as FileUploadStatus }
                    : fp
                )
              );
              cancelUploadRequestedRef.current = true;
              batchAbortControllerRef.current?.abort();
              throw error;
            }

            errorCount += 1;
            pendingProgressRef.current.delete(i);
            const errorMessage =
              error instanceof Error ? error.message : 'Upload failed';

            setFileProgress(prev =>
              prev.map((fp, idx) =>
                idx === i
                  ? {
                      ...fp,
                      status: 'error' as FileUploadStatus,
                      error: errorMessage,
                    }
                  : fp
              )
            );
          } finally {
            uploadAbortControllersRef.current.delete(i);
            activeIndices.delete(i);
            updateCurrentUploadingIndex();
          }
        },
        { concurrency: maxConcurrent, signal: batchAbortControllerRef.current.signal }
      );

      batchAbortControllerRef.current = null;

      if (cancelUploadRequestedRef.current) {
        setFileProgress(prev =>
          prev.map(fp =>
            fp.status === 'pending' || fp.status === 'uploading'
              ? { ...fp, status: 'cancelled' as FileUploadStatus }
              : fp
          )
        );
        setCurrentUploadingFileIndex(undefined);
        return;
      }

      // Invalidate study files list
      refetch();

      // Show appropriate toast messages
      if (errorCount === 0) {
        toast.success(`${selectedFiles.length} file(s) uploaded successfully`, {
          description: 'File(s) are ready for use',
        });
        // Close modal after a short delay only if all files uploaded successfully
        setTimeout(() => {
          setShowUploadModal(false);
          setSelectedFiles([]);
          setFileProgress([]);
          setCurrentUploadingFileIndex(undefined);
        }, 1500);
      } else if (successCount === 0) {
        toast.error(`Failed to upload all ${selectedFiles.length} files`, {
          description: 'Please check files and try again',
        });
        // Don't close modal automatically if all files failed - let user see errors
      } else {
        toast.warning(`${successCount} of ${selectedFiles.length} files uploaded`, {
          description: `${errorCount} files failed to upload`,
        });
        // Don't close modal automatically if some files failed - let user see errors
      }
    } catch (error) {
      toast.error('Upload failed', {
        description:
          error instanceof Error ? error.message : 'Please try again',
      });
      setFileProgress([]);
      setCurrentUploadingFileIndex(undefined);
    }
  }, [selectedFiles, refetch, throttledProgressUpdate]);

  const uploadFilesByIndices = useCallback(
    async (indices: number[]) => {
      if (indices.length === 0) return;
      const isUploading = fileProgress.some(
        fp => fp.status === 'uploading' || fp.status === 'pending'
      );
      if (isUploading) return;

      cancelUploadRequestedRef.current = false;
      const indexSet = new Set(indices);
      const otherErrorCount = fileProgress.filter(
        (fp, idx) => fp.status === 'error' && !indexSet.has(idx)
      ).length;

      // Reset progress for the files we are retrying.
      setFileProgress(prev =>
        prev.map((fp, idx) =>
          indexSet.has(idx)
            ? {
                ...fp,
                status: 'pending' as FileUploadStatus,
                progress: 0,
                error: undefined,
              }
            : fp
        )
      );

      let retrySuccessCount = 0;
      let retryErrorCount = 0;
      const maxConcurrent = getUploadMaxConcurrent();
      const activeIndices = new Set<number>();

      const updateCurrentUploadingIndex = () => {
        if (activeIndices.size === 0) {
          setCurrentUploadingFileIndex(undefined);
          return;
        }
        setCurrentUploadingFileIndex(Math.min(...Array.from(activeIndices)));
      };

      uploadAbortControllersRef.current.clear();
      batchAbortControllerRef.current = new AbortController();

      await runPoolSettled(
        indices,
        async (i: number) => {
          if (cancelUploadRequestedRef.current) {
            throw new DOMException('Aborted', 'AbortError');
          }

          const file = selectedFiles[i];
          if (!file) return;

          activeIndices.add(i);
          updateCurrentUploadingIndex();

          setFileProgress(prev =>
            prev.map((fp, idx) =>
              idx === i
                ? { ...fp, status: 'uploading' as FileUploadStatus, progress: 0 }
                : fp
            )
          );

          const controller = new AbortController();
          uploadAbortControllersRef.current.set(i, controller);

          try {
            await uploadStudyFile(
              { file },
              (progressEvent) => {
                if (progressEvent.total) {
                  const progress = Math.round(
                    (progressEvent.loaded * 100) / progressEvent.total
                  );
                  throttledProgressUpdate(i, progress);
                }
              },
              { signal: controller.signal }
            );

            retrySuccessCount += 1;
            pendingProgressRef.current.delete(i);
            setFileProgress(prev =>
              prev.map((fp, idx) =>
                idx === i
                  ? {
                      ...fp,
                      status: 'completed' as FileUploadStatus,
                      progress: 100,
                      error: undefined,
                    }
                  : fp
              )
            );
          } catch (error) {
            const isCancelled =
              (error instanceof Error &&
                (error.name === 'CanceledError' ||
                  error.name === 'AbortError')) ||
              (error &&
                typeof error === 'object' &&
                'code' in error &&
                (error as { code?: unknown }).code === 'ERR_CANCELED');

            if (isCancelled || cancelUploadRequestedRef.current) {
              pendingProgressRef.current.delete(i);
              setFileProgress(prev =>
                prev.map((fp, idx) =>
                  idx === i
                    ? { ...fp, status: 'cancelled' as FileUploadStatus }
                    : fp
                )
              );
              cancelUploadRequestedRef.current = true;
              batchAbortControllerRef.current?.abort();
              throw error;
            }

            retryErrorCount += 1;
            pendingProgressRef.current.delete(i);
            const errorMessage =
              error instanceof Error ? error.message : 'Upload failed';
            setFileProgress(prev =>
              prev.map((fp, idx) =>
                idx === i
                  ? {
                      ...fp,
                      status: 'error' as FileUploadStatus,
                      error: errorMessage,
                    }
                  : fp
              )
            );
          } finally {
            uploadAbortControllersRef.current.delete(i);
            activeIndices.delete(i);
            updateCurrentUploadingIndex();
          }
        },
        { concurrency: maxConcurrent, signal: batchAbortControllerRef.current.signal }
      );

      batchAbortControllerRef.current = null;

      if (cancelUploadRequestedRef.current) {
        setFileProgress(prev =>
          prev.map((fp, idx) =>
            indexSet.has(idx) && (fp.status === 'pending' || fp.status === 'uploading')
              ? { ...fp, status: 'cancelled' as FileUploadStatus }
              : fp
          )
        );
        setCurrentUploadingFileIndex(undefined);
        return;
      }

      setCurrentUploadingFileIndex(undefined);
      refetch();

      if (retryErrorCount === 0) {
        toast.success('Retry completed', {
          description: `Retried ${retrySuccessCount} file(s) successfully`,
        });
        if (otherErrorCount === 0) {
          setTimeout(() => {
            setShowUploadModal(false);
            setSelectedFiles([]);
            setFileProgress([]);
            setCurrentUploadingFileIndex(undefined);
          }, 1200);
        }
        return;
      }

      toast.warning('Retry completed with errors', {
        description: `${retryErrorCount} file(s) still failed to upload`,
      });
    },
    [fileProgress, refetch, selectedFiles, throttledProgressUpdate]
  );

  const handleRetryAllErrors = useCallback(() => {
    const indices = fileProgress
      .map((fp, idx) => (fp.status === 'error' ? idx : null))
      .filter((idx): idx is number => typeof idx === 'number');
    void uploadFilesByIndices(indices);
  }, [fileProgress, uploadFilesByIndices]);

  const handleRetryFile = useCallback(
    (fileIndex: number) => {
      if (fileProgress[fileIndex]?.status !== 'error') return;
      void uploadFilesByIndices([fileIndex]);
    },
    [fileProgress, uploadFilesByIndices]
  );

  const handleCancelUpload = useCallback(() => {
    cancelUploadRequestedRef.current = true;
    batchAbortControllerRef.current?.abort();
    uploadAbortControllersRef.current.forEach(controller => controller.abort());
    uploadAbortControllersRef.current.clear();

    setFileProgress(prev =>
      prev.map(fp =>
        fp.status === 'pending' || fp.status === 'uploading'
          ? { ...fp, status: 'cancelled' as FileUploadStatus }
          : fp
      )
    );
    setCurrentUploadingFileIndex(undefined);
    toast.info('Upload cancelled');
  }, []);

  const handleCloseUploadModal = useCallback(() => {
    const isUploading = fileProgress.some(
      fp => fp.status === 'uploading' || fp.status === 'pending'
    );
    if (!isUploading) {
      setShowUploadModal(false);
      setSelectedFiles([]);
      setFileProgress([]);
      setCurrentUploadingFileIndex(undefined);
    }
  }, [fileProgress]);

  const handleRemoveFileFromSelection = useCallback((index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setFileProgress(prev => prev.filter((_, i) => i !== index));
  }, []);

  useEffect(() => {
    if (showUploadModal && selectedFiles.length === 0) {
      setShowUploadModal(false);
    }
  }, [showUploadModal, selectedFiles.length]);

  // Direct upload handler
  const handleUploadButtonClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Drag and drop handlers
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      handleFilesSelected(acceptedFiles);
    },
    [handleFilesSelected]
  );

  const onDragEnter = useCallback(() => setDragActive(true), []);
  const onDragLeave = useCallback(() => setDragActive(false), []);
  const onDropAccepted = useCallback(() => setDragActive(false), []);
  const onDropRejected = useCallback(() => setDragActive(false), []);

  const dropzoneConfig = useMemo(
    () => ({
      onDrop,
      noClick: true,
      noKeyboard: true,
      multiple: true,
      onDragEnter,
      onDragLeave,
      onDropAccepted,
      onDropRejected,
    }),
    [onDrop, onDragEnter, onDragLeave, onDropAccepted, onDropRejected]
  );

  const { getRootProps, getInputProps, isDragActive } =
    useDropzone(dropzoneConfig);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        handleFilesSelected(Array.from(e.target.files));
        e.target.value = '';
      }
    },
    [handleFilesSelected]
  );

  // Memoize actions to prevent unnecessary re-renders
  const tableActions = useMemo(
    () => (
      <Flex gap="2" align="center">
        {/* Direct Upload Button */}
        <Button
          startIcon={<IconUpload />}
          variant="classic"
          size="small"
          color="primary"
          onClick={handleUploadButtonClick}
          disabled={fileProgress.some(fp => fp.status === 'uploading' || fp.status === 'pending')}
          loading={fileProgress.some(fp => fp.status === 'uploading')}
        >
          {fileProgress.some(fp => fp.status === 'uploading') ? 'Uploading...' : 'Upload Files'}
        </Button>

        {/*  Refresh Button
        <Button
          startIcon={<IconRefresh />}
          variant="soft"
          size="small"
          color="default"
          onClick={handleRefresh}
          disabled={isLoading}
        >
          Refresh
        </Button>*/}
      </Flex>
    ),
    [handleUploadButtonClick, fileProgress]
  );

  return (
    <Flex
      {...getRootProps()}
      className={className}
      style={{
        position: 'relative',
        minHeight: '100%',
        width: '100%',
        justifyContent: 'stretch',
      }}
    >
      <input {...getInputProps()} />

      {/* Drag and drop overlay */}
      {(dragActive || isDragActive) && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundImage:
              'linear-gradient(to bottom, var(--accent-2), var(--accent-3))',
            backdropFilter: 'blur(4px)',
          }}
        >
          <EmptyContent
            withIconBox
            icon={<IconCloudUpload style={{ color: 'var(--accent-9)' }} />}
            title="Drop files here to upload"
            description="Files will be uploaded immediately to study files storage"
            size="large"
            disableAnimation
          />
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: 'none' }}
        multiple
        onChange={handleFileInputChange}
      />

      <Flex
        direction="column"
        gap="4"
        style={{ height: 'inherit', width: 'inherit' }}
      >
        {/* Delete errors */}
        {deleteError && (
          <Alert color="error" title="Delete Error">
            {deleteError}
          </Alert>
        )}

        {/* General API errors */}
        {error && (
          <Alert
            color="error"
            title="Failed to load study files. Please try again."
            variant="soft"
            icon={<IconAlertTriangleFilled />}
          ></Alert>
        )}

        {/* Files table */}
        {/* Table */}
        <StudyFilesTable
          files={files}
          loading={isLoading}
          onDeleteFile={handleDeleteFile}
          onRefresh={handleRefresh}
          pagination={effectivePaginationInfo}
          paginationState={pagination}
          onPaginationChange={handlePaginationChange}
          actions={tableActions}
          onUploadClick={handleUploadButtonClick}
        />
      </Flex>

      {/* Upload Confirmation Modal */}
      <StudyFilesUploadConfirmationModal
        isOpen={showUploadModal}
        onClose={handleCloseUploadModal}
        onConfirm={handleConfirmUpload}
        onCancelUpload={handleCancelUpload}
        onRetryAllErrors={handleRetryAllErrors}
        onRetryFile={handleRetryFile}
        onRemoveFile={handleRemoveFileFromSelection}
        files={selectedFiles}
        isUploading={fileProgress.some(fp => fp.status === 'uploading' || fp.status === 'pending')}
        fileProgress={fileProgress}
        currentUploadingFileIndex={currentUploadingFileIndex}
      />
    </Flex>
  );
};

export default StudyFilesManager;
